import React, { memo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SignalQuality from "../icons/SignalQuality";
import {
  ResultSummarySection,
} from "./ResultSummarySection";
import ResultQualityNotice from "./ResultQualityNotice";
import {
  RECORDING_QUALITY_OVERALL_STATES,
  getRecordingQualityStatusLabel,
  getRecordingQualitySummaryCopy,
} from "../../utils/results/recordingQuality";

function SummaryDot({ tone }) {
  const toneClassName =
    tone === RECORDING_QUALITY_OVERALL_STATES.PASSED
      ? "bg-green-800"
      : tone === RECORDING_QUALITY_OVERALL_STATES.REPEAT
        ? "bg-amber-800"
        : null;

  if (!toneClassName) {
    return null;
  }

  return (
    <span
      className={["mt-px h-2 w-2 shrink-0 rounded-full self-center", toneClassName].join(" ")}
      aria-hidden="true"
    />
  );
}

function GuidanceLink({ to, label, onClick }) {
  if (!to || !label) {
    return null;
  }

  return (
    <Link
      to={to}
      className="self-end inline-flex items-center gap-0.5 text-sm font-medium text-primary"
      onClick={onClick || undefined}
    >
      {label}
      <span
        className="material-symbols-outlined text-[18px] leading-none"
        aria-hidden="true"
      >
        chevron_right
      </span>
    </Link>
  );
}

function RecordingQualityRow({ check, t }) {
  const statusLabel = getRecordingQualityStatusLabel(t, check.state);
  const statusClassName =
    check.state === "fail" ? "!text-amber-700" : "!text-typography";

  return (
    <div className="result-summary-row">
      <div className="result-summary-row-label !text-typography">
        {check.label}
      </div>
      <div
        className={[
          "result-summary-row-value !text-base !font-medium !leading-relaxed",
          statusClassName,
        ].join(" ")}
      >
        <span>{statusLabel}</span>
      </div>
    </div>
  );
}

function RecordingQualitySection({
  summary,
  guidanceLinkTo = "",
  guidanceCtaLabel = "",
  repeatLinkTo = "",
  repeatCtaLabel = "",
  onGuidanceClick = null,
  hints = [],
  hintsTitle = "",
}) {
  const { t } = useTranslation();

  if (!summary?.checks?.length) {
    return null;
  }

  const summaryCopy = getRecordingQualitySummaryCopy(t, summary);
  const showBreakdown =
    summary.overallState !== RECORDING_QUALITY_OVERALL_STATES.PASSED &&
    summary.sortedChecks.length > 0;
  const showGuidance =
    summary.overallState !== RECORDING_QUALITY_OVERALL_STATES.PASSED;
  const noticeHints = Array.isArray(hints) ? hints.filter(Boolean) : [];
  const showRepeatNotice =
    summary.overallState === RECORDING_QUALITY_OVERALL_STATES.REPEAT;
  const showSummary = Boolean(summaryCopy) && !showRepeatNotice;
  const breakdownContent = showBreakdown ? (
    <div className="flex w-full flex-col gap-4">
      {summary.sortedChecks.map((check) => (
        <RecordingQualityRow key={check.key} check={check} t={t} />
      ))}
    </div>
  ) : null;

  if (showRepeatNotice) {
    return (
      <ResultQualityNotice
        messages={[]}
        hints={noticeHints}
        headerTitle={summaryCopy?.title || ""}
        bodyText={summaryCopy?.body || ""}
        detailContent={breakdownContent}
        showDetailsLabel={t(
          "result.recordingQuality.showDetails",
          "Show quality details",
        )}
        hideDetailsLabel={t(
          "result.recordingQuality.hideDetails",
          "Hide quality details",
        )}
        hintsTitle={hintsTitle}
        linkTo={guidanceLinkTo}
        repeatLinkTo={repeatLinkTo}
        ctaLabel={guidanceCtaLabel}
        repeatCtaLabel={repeatCtaLabel}
        onLinkClick={onGuidanceClick}
      />
    );
  }

  return (
    <ResultSummarySection
      icon={<SignalQuality />}
      title={t("result.qualityTitle", "Recording Quality")}
    >
      <div className="flex w-full flex-col gap-3">
        {showSummary ? (
          <div
            className="flex w-full flex-col gap-1"
            aria-label={summaryCopy.accessibleLabel}
          >
            {summaryCopy.title ? (
              <div className="flex w-full items-center gap-2">
                <SummaryDot tone={summaryCopy.tone} />
                <p className="min-w-0 text-sm font-medium leading-relaxed whitespace-normal text-typography">
                  {summaryCopy.title}
                </p>
              </div>
            ) : null}
            {summaryCopy.body ? (
              <p className="text-sm leading-relaxed whitespace-normal text-typography">
                {summaryCopy.body}
              </p>
            ) : null}
          </div>
        ) : null}

        {breakdownContent}

        {!showRepeatNotice && showGuidance ? (
          <GuidanceLink
            to={guidanceLinkTo}
            label={guidanceCtaLabel}
            onClick={onGuidanceClick}
          />
        ) : null}
      </div>
    </ResultSummarySection>
  );
}

export default memo(RecordingQualitySection);
